import type { AddResourceRequest, Resource, UpdateResourceRequest, PageResponse } from '@/types/resource';
import { ResourceType, ResourceVisibility } from '@/types/resource';
import { useAuthStore } from '@/store/auth';
import { request } from '@/utils/request';

/**
 * 转换资源数据，确保类型正确
 */
function convertResource(data: any): Resource {
  if (!data) return data;
  
  return {
    ...data,
    type: data.type as ResourceType,
    visibility: data.visibility as ResourceVisibility,
    company: data.company,
    jobPosition: data.jobPosition,
    tags: data.tags || []
  };
}

/**
 * 转换资源列表数据
 */
function convertPageResponse(response: any): PageResponse<Resource> {
  if (!response) return response;

  return {
    ...response,
    content: (response.content || []).map(convertResource)
  };
}

/**
 * 资源管理相关API服务
 */
export default {
  /**
   * 新增资源记录
   * @param data 资源信息
   * @returns 添加结果
   */
  async addResource(data: AddResourceRequest): Promise<Resource> {
    const response = await request.post<Resource>('/resources', data);
    return response;
  },

  /**
   * 更新资源记录
   * @param id 资源ID
   * @param data 更新数据
   * @returns 更新结果
   */
  async updateResource(id: number, data: UpdateResourceRequest): Promise<Resource> {
    const response = await request.put<Resource>(`/resources/${id}`, data);
    return response;
  },

  /**
   * 删除资源记录
   * @param id 资源ID
   * @returns 删除结果
   */
  async deleteResource(id: number): Promise<Resource> {
    const response = await request.delete<Resource>(`/resources/${id}`);
    return response;
  },

  /**
   * 获取资源记录详情
   * @param id 资源ID
   * @returns 资源详情
   */
  async getResourceById(id: number): Promise<Resource> {
    const response = await request.get<Resource>(`/resources/${id}`);
    return response;
  },

  /**
   * 根据职位获取资源列表
   * @param positionId 职位ID
   * @param page 页码（从1开始）
   * @param size 每页大小
   * @returns 资源列表
   */
  async getResourcesByPosition(positionId: number, page = 1, size = 10): Promise<PageResponse<Resource>> {
    try {
      const response = await request.get<PageResponse<Resource>>(`/resources/position/${positionId}`, {
        params: { page, size }
      });
      return response;
    } catch (error) {
      console.error('获取职位资源列表失败:', error);
      throw error;
    }
  },

  /**
   * 根据公司获取资源列表
   * @param companyId 公司ID
   * @param page 页码（从1开始）
   * @param size 每页大小
   * @returns 资源列表
   */
  async getResourcesByCompany(companyId: number, page = 1, size = 10): Promise<PageResponse<Resource>> {
    try {
      const response = await request.get<PageResponse<Resource>>(`/resources/company/${companyId}`, {
        params: { page, size }
      });
      return response;
    } catch (error) {
      console.error('获取公司资源列表失败:', error);
      throw error;
    }
  }
};